import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import "./index.scss";
import PageHeader from "../../../adminCompenents/listComponents/Layout";
import CardContainer from "../../../adminCompenents/listComponents/UI/CardContainer";
import ExportButton from "../../../adminCompenents/listComponents/UI/ExportButton";
import DataTable from "../../../adminCompenents/listComponents/UI/DataTable";
import Pagination from "../../../adminCompenents/listComponents/UI/Pagination";
import DataCards from "../../../adminCompenents/listComponents/UI/DataCards";
import { showErrorToaster } from "../../../adminCompenents/listComponents/UI/toaster/error";
import { exportCSV } from "../../../adminCompenents/utils";
import OrderService from "../../../services/order";
import UserService from "../../../services/user";
import OrderSearchFilterBar from "./OrderSearchFilterBar";
import { Form, Spinner } from "react-bootstrap";

const orderService = new OrderService();
const userService = new UserService();

const OrdersPage = () => {
  const navigate = useNavigate();
  const [orders, setOrders] = useState([]);
  const [clients, setClients] = useState([]);
  const [search, setSearch] = useState("");
  const [filters, setFilters] = useState({});
  const [page, setPage] = useState(1);
  const perPage = 5;
  const [isMobile, setIsMobile] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  // 👉 Responsive
  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    handleResize();
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // 👉 Charger les commandes
  const loadOrders = async () => {
    setIsLoading(true);
    try {
      const response = await orderService.getAllOrders();
      setOrders(
        Array.isArray(response.data) ? response.data : response.data?.data || []
      );
    } catch (error) {
      showErrorToaster("Erreur lors du chargement des commandes");
    } finally {
      setIsLoading(false);
    }
  };

  // 👉 Charger les clients pour le filtre
  const loadClients = async () => {
    try {
      const res = await userService.getClients();
      setClients(res.data);
    } catch (error) {
      console.error("Erreur lors du chargement des clients :", error);
    }
  };

  useEffect(() => {
    loadOrders();
    loadClients();
  }, []);

  // 👉 Filtrer les commandes
  const filtered = orders.filter((o) => {
    const matchSearch =
      o.nom?.toLowerCase().includes(search.toLowerCase()) ||
      o.prenom?.toLowerCase().includes(search.toLowerCase()) ||
      o.ville?.toLowerCase().includes(search.toLowerCase());

    const matchStatus = filters.status
      ? o.status?.toLowerCase() === filters.status.toLowerCase()
      : true;
    const matchVille = filters.ville
      ? o.ville?.toLowerCase().includes(filters.ville.toLowerCase())
      : true;
    const matchNom = filters.nom
      ? o.nom?.toLowerCase().includes(filters.nom.toLowerCase())
      : true;
    const matchPrenom = filters.prenom
      ? o.prenom?.toLowerCase().includes(filters.prenom.toLowerCase())
      : true;
    const matchClient = filters.userId
      ? o.userId?._id === filters.userId
      : true;

    return (
      matchSearch &&
      matchStatus &&
      matchVille &&
      matchNom &&
      matchPrenom &&
      matchClient
    );
  });

  const totalPages = Math.ceil(filtered.length / perPage);
  const paginated = filtered.slice((page - 1) * perPage, page * perPage);

  // 👉 Export CSV
  const handleExport = () => {
    const data = orders.map((order) => ({
      Nom: order.nom,
      Prénom: order.prenom,
      Ville: order.ville,
      "Code Postal": order.codePostal,
      Téléphone: order.numero,
      Statut: order.status,
      "Total (après promo)": `${order.total} DT`,
      "Total avant promo": `${order.totalA} DT`,
      "Date de création": new Date(order.createdAt).toLocaleDateString("fr-FR"),
    }));
    exportCSV(
      [
        "Nom",
        "Prénom",
        "Ville",
        "Code Postal",
        "Téléphone",
        "Statut",
        "Total (après promo)",
        "Total avant promo",
        "Date de création",
      ],
      data,
      "commandes.csv"
    );
  };

  const columns = [
    {
      header: "Client",
      accessor: "nom",
      Cell: (value, row) => (
        <div>
          <div className="fw-bold">
            {row.nom} {row.prenom}
          </div>
          <small className="text-muted">{row?.userId?.name}</small>
        </div>
      ),
    },
    { header: "Ville", accessor: "ville", Cell: (value) => value || "N/A" },
    {
      header: "Statut",
      accessor: "status",
      Cell: (value, row) => {
        const [status, setStatus] = useState(value);
        const [updating, setUpdating] = useState(false);

        const handleStatusChange = async (e) => {
          const newStatus = e.target.value;
          setStatus(newStatus);
          setUpdating(true);
          try {
            const res = await orderService.changeOrderStatus(
              row._id,
              newStatus
            );
            setStatus(res.data.status);
          } catch (err) {
            showErrorToaster("Erreur lors du changement de statut");
          } finally {
            setUpdating(false);
          }
        };

        const statusColors = {
          pending: "warning",
          paid: "success",
          cancelled: "secondary",
          in_delivery: "info",
        };

        return (
          <Form.Select
            value={status}
            onChange={handleStatusChange}
            disabled={updating}
            className={`text-white bg-${statusColors[status]}`}
            style={{ width: "180px" }}>
            <option value="pending">En attente</option>
            <option value="paid">Payée</option>
            <option value="cancelled">Annulée</option>
            <option value="in_delivery">En cours de livraison</option>
          </Form.Select>
        );
      },
    },
    { header: "Total", accessor: "total", Cell: (value) => `${value} DT` },
    {
      header: "Produits",
      accessor: "products",
      Cell: (products) =>
        products && products.length > 0 ? (
          <ul className="mb-0">
            {products.map((p, i) => (
              <li key={i}>
                {p.title} (x{p.quantity})
              </li>
            ))}
          </ul>
        ) : (
          "Aucun produit"
        ),
    },
    {
      header: "Date",
      accessor: "createdAt",
      Cell: (value) =>
        value ? new Date(value).toLocaleDateString("fr-FR") : "N/A",
    },
  ];

  const rowActions = (row) => [
    { label: "Voir", onClick: () => navigate(`/details-order/${row._id}`) },
  ];

  return (
    <div>
      <PageHeader
        title="Commandes"
        breadcrumb={[{ label: "Accueil", href: "/" }, { label: "Commandes" }]}
        isLoading={isLoading}
      />

      <CardContainer>
        <div className="d-flex justify-content-between align-items-center mb-3 m-4">
          <h5 className="title-liste">Liste des commandes</h5>
          <div className="d-flex gap-2">
            <ExportButton onClick={handleExport} />
          </div>
        </div>
        <hr className="hr-table" />

        {/* ✅ Search & Filter Bar */}
        <OrderSearchFilterBar
          onSearch={setSearch}
          onFilter={setFilters}
          clients={clients}
        />

        <hr className="hr-table" />

        {/* ✅ Responsive affichage */}
        {isMobile ? (
          <DataCards
            columns={columns}
            data={paginated}
            keyField="_id"
            rowActions={rowActions}
          />
        ) : (
          <DataTable
            columns={columns}
            data={paginated}
            keyField="_id"
            rowActions={rowActions}
          />
        )}

        <Pagination
          currentPage={page}
          totalPages={totalPages}
          onPageChange={setPage}
        />
      </CardContainer>
    </div>
  );
};

export default OrdersPage;
